import * as React from "react";

import * as NavigationMenu from "@radix-ui/react-navigation-menu";
import { SECTION_CATEGORIES } from "@v1/components/left-bar/sections-pane/TemplateCategories";
import TemplateCategoryMenuCard from "@v1/components/left-bar/sections-pane/TemplateCategoryMenuCard";
import TemplatesList from "@v1/components/left-bar/sections-pane/TemplateList";
import useFigmaPluginPaste from "@v1/hooks/figmaToReplo/useFigmaPluginPaste";
import useCurrentDragType from "@v1/hooks/useCurrentDragType";
import { useFindParentForPaste } from "@v1/hooks/useFindParentForPaste";
import {
  selectLeftBarNavigationOpenedMenu,
  setLeftBarNavigationOpenedMenu,
} from "@v1/reducers/ui-reducer";
import { useEditorDispatch, useEditorSelector } from "@v1/store/store";

const FigmaCard = () => {
  const findParentForPaste = useFindParentForPaste();
  const { pasteFromFigmaOrToast } = useFigmaPluginPaste({
    findParentForPaste,
  });

  return (
    <button
      className="hover:bg-sheet flex w-full items-center justify-between rounded-sm p-2 text-xs font-semibold"
      onClick={() => void pasteFromFigmaOrToast()}
    >
      <div className="flex items-center gap-2">
        <div className="bg-surface border-0.5 border-border rounded-sm p-2">
          <img
            className="h-[18px] w-[18px]"
            src="/images/figma/figma-logo.svg"
            alt="Figma logo"
          />
        </div>
        Import from Figma
      </div>
    </button>
  );
};

const TemplatesMenu = ({ searchString }: { searchString: string }) => {
  const dispatch = useEditorDispatch();
  const { currentDragType } = useCurrentDragType();
  const leftBarNavigationOpenedMenu = useEditorSelector(
    selectLeftBarNavigationOpenedMenu,
  );
  // NOTE (Fran 2024-07-09): We must prevent the user from opening different categories when moving
  // the mouse to the right diagonally. This is to prevent the user from accidentally opening a category
  // when trying to go to the menu content to drag a component.
  const [allowOpen, setAllowOpen] = React.useState(true);

  const handleOpenMenu = React.useCallback(
    (value: string) => {
      // NOTE (Fran 2024-07-09): We should open the menu when is allowed to open. This is to prevent the user
      // from accidentally opening a category when trying to go to the menu content to drag a component.
      if (allowOpen) {
        dispatch(setLeftBarNavigationOpenedMenu(value));
      }
    },
    [allowOpen, dispatch],
  );

  // NOTE (Sebas, 2024-06-26): We need to hide the component template pane when
  // dragging a new component.
  React.useEffect(() => {
    if (currentDragType) {
      setAllowOpen(false);
    } else {
      dispatch(setLeftBarNavigationOpenedMenu(""));
      setAllowOpen(true);
    }
  }, [currentDragType, dispatch]);

  // NOTE (Ben O., 2025-03-04): This code prevents users from opening different
  // categories when moving the mouse to the right diagonally while going to
  // the menu content.
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const clearTimeoutRef = React.useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);
  const handleMouseMove = React.useCallback(
    (e: React.MouseEvent) => {
      // Don't allow changes to allowOpen when dragging
      if (currentDragType) {
        return;
      }

      if (e.movementX < 3) {
        clearTimeoutRef();
        setAllowOpen(true);
      } else if (timeoutRef.current === null) {
        setAllowOpen(false);
        timeoutRef.current = setTimeout(() => {
          // Only set allowOpen to true if we're not dragging
          if (!currentDragType) {
            setAllowOpen(true);
          }
          timeoutRef.current = null;
        }, 1000);
      }
    },
    [clearTimeoutRef, currentDragType],
  );

  React.useEffect(() => {
    return clearTimeoutRef;
  }, [clearTimeoutRef]);

  if (searchString) {
    return (
      <div
        className="no-scrollbar absolute inset-0 mt-[76px] flex flex-col overflow-y-scroll bg-transparent p-3"
        id="sections-pane-search-wrapper"
      >
        <TemplatesList searchString={searchString} />
      </div>
    );
  }

  return (
    <>
      <NavigationMenu.Root
        value={leftBarNavigationOpenedMenu}
        onValueChange={handleOpenMenu}
        onMouseMove={handleMouseMove}
      >
        <NavigationMenu.List className="mb-12 flex flex-col">
          {SECTION_CATEGORIES.map((category) => (
            <TemplateCategoryMenuCard
              key={category.value}
              category={category}
              openedMenu={leftBarNavigationOpenedMenu}
              hideMenuContent={Boolean(currentDragType)}
            />
          ))}
          <FigmaCard />
        </NavigationMenu.List>

        <NavigationMenu.Viewport />
      </NavigationMenu.Root>
    </>
  );
};

export default TemplatesMenu;
